import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { loadConfiguration } from '@/config/configuration';
import '@/i18n';
import 'hestia-css';
import 'hestia-fonts';

/**
 * Deux chunks lazy distincts :
 *  - legal  : page publique /{appCode} — sans MSAL, légère
 *  - portal : app authentifiée /portal/** — MSAL + admin
 */
const LegalDocumentsPage = lazy(() => import('@/features/legal/LegalDocumentsPage'));
const PortalApp = lazy(() => import('@/features/portal/PortalApp'));
const AuthRedirectHandler = lazy(() => import('@/features/portal/auth/AuthRedirectHandler'));

/** Vrai si l'URL porte une réponse de redirection MSAL (fragment #code=… / #error=…). */
function hasMsalRedirectResponse(): boolean {
  return /[#&](code|error|state|id_token|access_token|client_info)=/.test(window.location.hash);
}

/** Compatibilité avec les anciennes URLs hash (/#/admin, /#/uploadDocument…). */
function LegacyHashRedirect() {
  const hash = window.location.hash;
  if (hash.startsWith('#/uploadDocument')) return <Navigate to="/portal/admin/upload" replace />;
  if (hash.startsWith('#/updateDocument')) return <Navigate to="/portal/admin" replace />;
  if (hash.startsWith('#/admin')) return <Navigate to="/portal/admin" replace />;
  return <Navigate to="/portal" replace />;
}

/**
 * Route racine "/". Sert aussi de cible de redirection MSAL (redirectUri = origin) :
 * si l'URL porte une réponse Entra ID, on monte AuthRedirectHandler (donc MsalProvider)
 * pour la traiter au lieu de rediriger et de perdre le fragment.
 */
function RootRoute() {
  if (hasMsalRedirectResponse()) return <AuthRedirectHandler />;
  return <LegacyHashRedirect />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

loadConfiguration()
  .then(() => {
    createRoot(document.getElementById('app-root')!).render(
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/portal/*" element={<PortalApp />} />
              <Route path="/:appCode" element={<LegalDocumentsPage />} />
              <Route path="*" element={<Navigate to="/portal" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    );
  })
  .catch((error: unknown) => {
    const root = document.getElementById('app-root');
    if (root) root.textContent = 'Erreur de chargement de la configuration.';
    console.error(error);
  });
